import React, { useEffect, useMemo, useState } from "react";
import { supabase } from "@/lib/supabase";
import { useAppContext } from "@/contexts/AppContext";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "@/components/ui/use-toast";
import { buildRateContext, computeDriverPortion, describeWeekArrangements, effectivePercent, flatCommissionPercent, gstRateFraction, insuranceExcessAmount, weekSummaryKind, type ResolvedRateContext } from "@/lib/paymentRates";
import { SUMMARY_COPY, type SummarySegment } from "@/lib/summaryKind";
import { loadPayoutColumnMap, DEFAULT_PAYOUT_COLUMN_MAP, loadDeductionCategories, loadPaymentCategories } from "@/lib/settingsConfig";
import { loadPayslipConfig, DEFAULT_PAYSLIP_CONFIG, type PayslipConfig } from "@/lib/payslipConfig";
import { buildPayslipHtml, buildPaymentSummaryPdf, type PayslipDeductionLine, type PayslipData } from "@/lib/payslip";
import { loadWeekDeductionLines } from "@/lib/driverDeductions";
import type jsPDF from "jspdf";
import { FileText, Mail } from "lucide-react";

const DEFAULT_TENANT_ID = "1225f4cb-43ae-463d-a6b0-53f721362dbf";
const DOCS_BUCKET = "driver_documents";

/* ---------- helpers ---------- */
const money = (n: number | null | undefined) => `$${(Number(n || 0)).toFixed(2)}`;
const sanitizeForPath = (s: string) => s.replace(/[^a-zA-Z0-9_-]/g, "_");
const summaryFileName = (driverName: string, weekStart: string, weekEnd: string) =>
  `Payment-Summary-${sanitizeForPath(driverName)}-${weekStart}-to-${weekEnd}.pdf`;

/** Uploads the built PDF to the same driver_documents bucket used for DL/ID
 *  scans, so a driver's payment summary is a real file, not just an HTML
 *  string re-opened in a print window. Non-fatal on failure -- the summary
 *  itself is still saved either way, just without a downloadable PDF link. */
const uploadSummaryPdf = async (doc: jsPDF, driverName: string, weekStart: string, weekEnd: string): Promise<string | null> => {
  try {
    const blob = doc.output("blob");
    const path = `drivers/${sanitizeForPath(driverName)}/PaymentSummary_${weekStart}_to_${weekEnd}.pdf`;
    const { error } = await supabase.storage.from(DOCS_BUCKET).upload(path, blob, { upsert: true, contentType: "application/pdf" });
    if (error) throw error;
    return supabase.storage.from(DOCS_BUCKET).getPublicUrl(path).data.publicUrl;
  } catch (err) {
    console.error("Error uploading payment summary PDF:", err);
    return null;
  }
};
const parseISO = (s: string) => {
  const [y, m, d] = s.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  dt.setHours(0, 0, 0, 0);
  return dt;
};
const fmtISO = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const listDates = (startISO: string, endISO: string) => {
  const out: string[] = [];
  const s = parseISO(startISO);
  const e = parseISO(endISO);
  for (let d = new Date(s); d <= e; d.setDate(d.getDate() + 1)) {
    out.push(fmtISO(d));
  }
  return out;
};

/* payout report columns */
const COLS = [
  { key: "cash_amt", label: "Cash" },
  { key: "eftpos_amt", label: "EFTPOS" },
  { key: "totalmobility_amt", label: "Total Mobility" },
  { key: "tm_hoist_amt", label: "TM Hoist" },
  { key: "discount", label: "Discount" }, // paid to driver
  { key: "account_amt", label: "Account" },
  { key: "other_amt", label: "Other" },
  { key: "ecard_amt", label: "eCard" },
] as const;

type JobRow = {
  pu_date: string | null;
  cash_amt: number | null;
  eftpos_amt: number | null;
  totalmobility_amt: number | null;
  tm_hoist_amt: number | null;
  account_amt: number | null;
  other_amt: number | null;
  ecard_amt: number | null;
  discount: number | null;
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
  driverName: string;
  weekStart: string; // YYYY-MM-DD (Mon)
  weekEnd: string;   // YYYY-MM-DD (Sun)
};

const DriverSummaryModal: React.FC<Props> = ({ isOpen, onClose, driverName, weekStart, weekEnd }) => {
  const { adminSettings, calculateBonus } = useAppContext() as any;

  const [loading, setLoading] = useState(false);
  const [jobs, setJobs] = useState<JobRow[]>([]);
  const [approved, setApproved] = useState(false);
  const [sendEmail, setSendEmail] = useState(false);
  const [note, setNote] = useState("");

  // week adjustments
  const [deductions, setDeductions] = useState(0);
  const [credits, setCredits] = useState(0);
  const [insurance, setInsurance] = useState(0);
  const [deductionLines, setDeductionLines] = useState<PayslipDeductionLine[]>([]);
  const [driverEmail, setDriverEmail] = useState<string | null>(null);
  const [sending, setSending] = useState(false);

  // DB-driven per-payment-type + per-driver rate context
  const [rateCtx, setRateCtx] = useState<ResolvedRateContext | null>(null);

  // DB-driven column -> payment-type mapping (Settings → Payment Types)
  const [columnMap, setColumnMap] = useState<Record<string, string | null>>(DEFAULT_PAYOUT_COLUMN_MAP);
  useEffect(() => {
    loadPayoutColumnMap(DEFAULT_TENANT_ID).then(setColumnMap).catch(() => {});
  }, []);

  // DB-driven payslip layout + branding (Settings → Payment Builder)
  const [psConfig, setPsConfig] = useState<PayslipConfig>(DEFAULT_PAYSLIP_CONFIG);
  useEffect(() => {
    loadPayslipConfig(DEFAULT_TENANT_ID).then(setPsConfig).catch(() => {});
  }, []);

  // The visible, ordered turnover columns (from the builder, falling back to
  // the default layout). Commission per column uses the builder's payment-type
  // mapping first, then the legacy column map, then the flat company %.
  const activeCols = useMemo(() => {
    const cols = (psConfig.columns || []).filter((c) => c.visible);
    return (cols.length ? cols : DEFAULT_PAYSLIP_CONFIG.columns).map((c) => ({
      key: c.key,
      label: c.label,
      paymentType: c.paymentType ?? columnMap[c.key] ?? null,
    }));
  }, [psConfig, columnMap]);


  // Per-payment-type driver-payable % for each visible column, shown
  // subscripted in the day-totals header so the driver can see exactly what
  // percentage of each payment type they are paid (their "Per Payment Type
  // Commission %"). Columns with no mapped payment type (e.g. "Other") show an
  // empty bracket "()" so it's clear no specific rate applies.
  const colPercentLabel = useMemo(() => {
    const flat = flatCommissionPercent(adminSettings);
    const out: Record<string, string> = {};
    activeCols.forEach((c) => {
      const ptName = c.paymentType;
      if (!ptName) {
        out[c.key] = "()";
        return;
      }
      // A driver who changes arrangement part-way through the week (commission -> lease)
      // is paid a different % before and after the change, so show each one in date order,
      // e.g. "(50% / 90%)". Otherwise there is just the one.
      const pcts: number[] = [];
      if (rateCtx) {
        const dates = listDates(weekStart, weekEnd);
        for (const day of dates) {
          const p = effectivePercent(rateCtx, driverName, ptName, flat, day);
          if (p != null && pcts[pcts.length - 1] !== p) pcts.push(p);
        }
      } else {
        pcts.push(flat);
      }
      out[c.key] = pcts.length === 0 ? "()" : `(${pcts.join("% / ")}%)`;
    });
    return out;
  }, [activeCols, rateCtx, driverName, adminSettings, weekStart, weekEnd]);



  useEffect(() => {
    if (!isOpen || !driverName) return;
    (async () => {
      setLoading(true);
      try {
        // jobs for driver — NEW RPC + NEW PARAM NAMES
        const { data, error } = await supabase.rpc("get_weekly_payout_jobs", {
          p_driver_names: [driverName],
          p_week_start: weekStart,
          p_week_end: weekEnd,
        });
        if (error) throw error;
        setJobs(((data || []) as any[]).map(r => ({
          pu_date: r.pu_date,
          cash_amt: r.cash_amt,
          eftpos_amt: r.eftpos_amt,
          totalmobility_amt: r.totalmobility_amt,
          tm_hoist_amt: r.tm_hoist_amt,
          account_amt: r.account_amt,
          other_amt: r.other_amt,
          ecard_amt: r.ecard_amt,
          discount: r.discount,
        })));

        // load deduction + payment category labels (DB-driven) for nice payslip labels
        let catLabel: Record<string, string> = {};
        try {
          const [cats, payCats] = await Promise.all([
            loadDeductionCategories(DEFAULT_TENANT_ID),
            loadPaymentCategories(DEFAULT_TENANT_ID),
          ]);
          catLabel = Object.fromEntries([...cats, ...payCats].map(c => [c.category_key, c.label]));
        } catch { /* fall back to raw keys */ }
        const labelFor = (key: string | null) =>
          (key && (catLabel[key] || key.replace(/_/g, " ").replace(/\b\w/g, m => m.toUpperCase()))) || "Deduction";

        // Insurance / deductions are now strictly per-driver: they ONLY apply
        // when THIS driver has a matching active adjustment row. We no longer
        // auto-apply the company-wide insurance excess to every driver — that
        // was wrongly showing deductions (e.g. $15 Insurance) for drivers who
        // have none configured.
        //
        // The lines come from the shared deduction logic (src/lib/driverDeductions.ts),
        // so this summary shows exactly what the Weekly Payout screen and bank
        // file do: for a reducing-balance charge, its short description, what
        // comes off this pay, what's been paid to date and what's left. A week
        // already exported reads from the ledger, so it never changes later.
        const weeklyIns = insuranceExcessAmount(adminSettings);
        let d = 0, c = 0, ins = 0;
        const lines: PayslipDeductionLine[] = [];
        const weekLines = await loadWeekDeductionLines({
          weekStart,
          weekEnd,
          driverName,
          insuranceWeekly: weeklyIns,
          labelFor,
        });
        weekLines.forEach((l) => {
          if (l.kind === "insurance") {
            ins += l.thisPay;
          } else if (l.kind === "deduction") {
            d += l.thisPay;
            lines.push({
              label: l.label,
              amount: l.thisPay,
              detail: l.detail ?? undefined,
              originalAmount: l.originalAmount ?? undefined,
              paidToDate: l.paidToDate ?? undefined,
              remaining: l.remaining ?? undefined,
            });
          } else {
            c += l.thisPay;
            lines.push({ label: l.label + " (credit)", amount: -l.thisPay });
          }
        });
        if (ins > 0) lines.unshift({ label: catLabel["insurance"] || "Insurance", amount: ins });

        // CRITICAL: push the computed adjustments into state so they actually
        // flow into the payment-summary KPIs. Previously these were calculated
        // but never stored, so every driver's "Deductions" showed $0.00 even
        // when they had recurring deductions/credits configured on their
        // profile. Wiring these setters makes the driver's financial
        // adjustments (full CRUD on the profile) automatically appear in their
        // next summary.
        setDeductions(d);
        setCredits(c);
        setInsurance(ins);
        setDeductionLines(lines);



        // driver email for "Send Email Summary"
        try {
          const { data: drv } = await supabase
            .from("drivers")
            .select("email")
            .ilike("driver_name", driverName)
            .limit(1)
            .maybeSingle();
          setDriverEmail((drv as any)?.email ?? null);
        } catch { setDriverEmail(null); }

        // Load DB-driven per-payment-type + per-driver rate context
        try {
          const ctx = await buildRateContext([driverName]);
          setRateCtx(ctx);
        } catch (rcErr) {
          console.error("Error loading rate context:", rcErr);
        }


      } catch (err: any) {
        console.error(err);
        toast({
          title: "Error loading summary",
          description: err?.message ?? String(err),
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    })();
  }, [isOpen, driverName, weekStart, weekEnd, adminSettings]);

  /* -------- aggregate by day & fill blanks -------- */
  const { days, totalsRow, kpis } = useMemo(() => {
    const allDays = listDates(weekStart, weekEnd);

    // base map with zeros (sums keyed by source column key)
    const map = new Map<string, { jobs: number; sums: Record<string, number> }>();
    for (const d of allDays) {
      const sums: Record<string, number> = {};
      activeCols.forEach(c => (sums[c.key] = 0));
      map.set(d, { jobs: 0, sums });
    }

    // add jobs. Previously any job whose pu_date fell outside the Mon–Sun
    // range (or had no date) was silently dropped — that made the modal's
    // job count (and turnover) disagree with the Weekly Payout list, which
    // counts every job the RPC returns. We now bucket out-of-range / undated
    // jobs into their own row so EVERY returned job is shown and totalled,
    // keeping the modal and the list in lock-step.
    for (const r of jobs) {
      const d = r.pu_date || "Undated";
      if (!map.has(d)) {
        const sums: Record<string, number> = {};
        activeCols.forEach(c => (sums[c.key] = 0));
        map.set(d, { jobs: 0, sums });
      }
      const agg = map.get(d)!;
      agg.jobs += 1;
      activeCols.forEach(c => (agg.sums[c.key] += Number((r as any)[c.key] || 0)));
    }

    // to array (keep dates sorted; "Undated" sinks to the bottom)
    const daysArr = Array.from(map.entries())
      .sort(([a], [b]) => (a === "Undated" ? 1 : b === "Undated" ? -1 : a.localeCompare(b)))
      .map(([date, agg]) => ({
        date,
        jobs: agg.jobs,
        sums: agg.sums,
        total: Object.values(agg.sums).reduce((s, v) => s + Number(v || 0), 0),
      }));


    // totals row
    const totals = {
      jobs: daysArr.reduce((s, d) => s + d.jobs, 0),
      sums: Object.fromEntries(
        activeCols.map(c => [
          c.key,
          daysArr.reduce((s, d) => s + Number(d.sums[c.key] || 0), 0),
        ])
      ) as Record<string, number>,
    };
    const gross = Object.values(totals.sums).reduce((s, v) => s + Number(v || 0), 0);

    // KPIs: ex-GST for commission; bonus based on GROSS turnover (matches
    // the "(based on gross)" label shown on the summary/payslip, and the
    // tier thresholds in Bonus Tier Management, which are round gross
    // figures). Previously this ran against exGst instead, which silently
    // dropped drivers into a lower tier than the screen implied.
    const gstRate = gstRateFraction(adminSettings);
    const flatCommission = flatCommissionPercent(adminSettings);

    // The DB-driven "% per payment type" is the DRIVER-PAYABLE share — the
    // portion of (ex-GST) turnover the driver keeps. So a HIGHER % pays the
    // driver MORE; the company's commission is what's left over. The payment
    // type per column now comes from the Payment Builder (falling back to the
    // legacy column map, then the flat company %).
    // Priced day by day, so a mid-week change of arrangement (commission -> lease) is honoured.
    const ptByCol: Record<string, string | null> = Object.fromEntries(activeCols.map((c) => [c.key, c.paymentType]));
    const { driverPortion, bonusEligibleGross, byKind, gstAmount, netTake } = computeDriverPortion({
      ctx: rateCtx,
      driverName,
      byDate: Object.fromEntries(daysArr.map((d) => [d.date, d.sums])),
      ptNameFor: (col) => ptByCol[col],
      gstRate,
      flatCommission,
      fallbackDate: weekEnd,
    });
    // Commission shown = the company's retained cut (the take the pricing is worked on minus
    // what the driver is paid).
    const commission = Math.max(0, netTake - driverPortion);

    // Commission summary, lease summary, or the combined one for the week a driver changes over.
    const kind = weekSummaryKind(rateCtx, driverName, weekStart, weekEnd);
    // A pure lease week doesn't count columns paid at 0% (e.g. the cash the driver keeps): they
    // show as a dash and stay out of the daily and grand totals, like the contract payment sheet.
    const excludedCols: string[] =
      kind === "lease" && rateCtx
        ? activeCols
            .filter((c) => c.paymentType && effectivePercent(rateCtx, driverName, c.paymentType, flatCommission, weekEnd) === 0)
            .map((c) => c.key)
        : [];
    const countedDayTotal = (sums: Record<string, number>) =>
      Object.entries(sums).reduce((s, [k, v]) => (excludedCols.includes(k) ? s : s + Number(v || 0)), 0);
    const daysOut = daysArr.map((d) => ({ ...d, total: countedDayTotal(d.sums) }));


    let bonus = 0;
    try {
      bonus = typeof calculateBonus === "function" ? Number(calculateBonus(bonusEligibleGross) || 0) : 0;
    } catch { bonus = 0; }

    const otherDeductions = Number(deductions || 0) - Number(credits || 0);
    const totalDeductions = Number(insurance || 0) + otherDeductions;

    // Payslip model (per requested layout)
    const driverPayment = driverPortion;            // driver-payable share (= net - commission)
    const totalPayment = driverPayment + bonus - totalDeductions;

    // Effective payout % the driver is paid (their share of the ex-GST take).
    // Shown subscripted in brackets so the driver can see what percentage of
    // the net take this payment represents, e.g. "Driver Payment (50%)".
    const driverPercent = netTake > 0 ? Math.round((driverPayment / netTake) * 100) : flatCommission;


    const segments: SummarySegment[] =
      kind === "mixed"
        ? [
            { label: "Commission days", netTake: byKind.commission.netTake, driverShare: byKind.commission.driverPortion },
            { label: "Lease days", netTake: byKind.lease.netTake, driverShare: byKind.lease.driverPortion },
          ]
        : [];

    // What was collected and counted: everything on commission days, only the counted columns on lease days.
    const collected = byKind.commission.grossTurnover + byKind.lease.netTake;

    return {
      days: daysOut,
      totalsRow: { jobs: totals.jobs, sums: totals.sums, total: countedDayTotal(totals.sums), excludedCols },
      kpis: {
        kind,
        segments,
        gross: collected,
        gstAmount,
        netTake,
        commission,
        driverPayment,
        driverPercent,
        deductions: totalDeductions,
        bonus,
        net: totalPayment,
      },
    };

  }, [jobs, weekStart, weekEnd, adminSettings, deductions, credits, insurance, rateCtx, activeCols, driverName, calculateBonus]);



  const buildPayslip = (): PayslipData => ({
    driverName,
    weekStart,
    weekEnd,
    periodNote: describeWeekArrangements(rateCtx, driverName, weekStart, weekEnd) ?? undefined,
    summaryKind: kpis.kind,
    segments: kpis.segments,
    grossTurnover: kpis.gross,
    gstAmount: kpis.gstAmount,
    netTake: kpis.netTake,
    commission: kpis.commission,
    driverPayment: kpis.driverPayment,
    bonus: kpis.bonus,
    deductionLines,
    totalDeductions: kpis.deductions,
    totalPayment: kpis.net,
    branding: {
      companyName: psConfig.branding.companyName,
      tagline: psConfig.branding.tagline,
      logoUrl: psConfig.branding.logoUrl,
      accentColor: psConfig.branding.accentColor,
      footerNote: psConfig.branding.footerNote,
      showDailyBreakdown: psConfig.branding.showDailyBreakdown,
    },
    elements: psConfig.elements,
    columns: activeCols.map(c => c.label),
    dailyRows: days.map(d => ({
      date: d.date,
      jobs: d.jobs,
      values: activeCols.map(c => (totalsRow.excludedCols.includes(c.key) ? 0 : Number(d.sums[c.key] || 0))),
      total: d.total,
    })),
    totalsRow: {
      jobs: totalsRow.jobs,
      values: activeCols.map(c => (totalsRow.excludedCols.includes(c.key) ? 0 : Number(totalsRow.sums[c.key] || 0))),
      total: totalsRow.total,
    },
  });

  const handleSavePdf = async () => {
    const doc = await buildPaymentSummaryPdf(buildPayslip());
    doc.save(summaryFileName(driverName, weekStart, weekEnd));
    toast({ title: "Payment summary downloaded", description: "Saved as a PDF file." });
  };

  const handleSendEmail = async (prebuiltDoc?: jsPDF | null) => {
    if (!driverEmail) {
      toast({
        title: "No email on file",
        description: `${driverName} has no email address saved. Add one in Driver Management.`,
        variant: "destructive",
      });
      return;
    }
    setSending(true);
    const doc = prebuiltDoc || (await buildPaymentSummaryPdf(buildPayslip()));
    const fileName = summaryFileName(driverName, weekStart, weekEnd);
    const payload = {
      to: driverEmail,
      driverEmail,
      driverName,
      period: `${weekStart} to ${weekEnd}`,
      subject: `${kpis.kind === "commission" ? "Weekly Payment Summary" : SUMMARY_COPY[kpis.kind].screenTitle} — ${driverName} (${weekStart} to ${weekEnd})`,
      pdfBase64: doc.output("datauristring").split(",")[1] || "",
      fileName,
    };
    try {
      const res = await supabase.functions.invoke("send-driver-payslip", { body: payload });
      if (res.error) throw res.error;
      toast({ title: "Email sent", description: `Payment summary emailed to ${driverEmail}.` });
    } catch (err: any) {
      // Graceful fallback: still download the PDF so it can be sent manually.
      doc.save(fileName);
      toast({
        title: "Could not auto-email",
        description: `Downloaded the PDF instead — email it to ${driverEmail} manually. (${err?.message ?? String(err)})`,
        variant: "destructive",
      });
    } finally {
      setSending(false);
    }
  };


  const persistSummary = async (): Promise<{ success: boolean; doc: jsPDF | null }> => {
    try {
      const data = buildPayslip();
      const html = buildPayslipHtml(data);
      const doc = await buildPaymentSummaryPdf(data);
      const pdfUrl = await uploadSummaryPdf(doc, driverName, weekStart, weekEnd);
      const payload = {
        driver_name: driverName,
        week_start: weekStart,
        week_end: weekEnd,
        jobs: totalsRow.jobs,
        gross: kpis.gross,
        gst_amount: kpis.gstAmount,
        net_take: kpis.netTake,
        commission: kpis.commission,
        driver_payment: kpis.driverPayment,
        bonus: kpis.bonus,
        total_deductions: kpis.deductions,
        total_payment: kpis.net,
        deduction_lines: deductionLines,
        payslip_html: html,
        pdf_url: pdfUrl,
        approved,
        note: note || null,
        updated_at: new Date().toISOString(),
      };
      const { error } = await supabase
        .from("driver_weekly_summaries")
        .upsert(payload, { onConflict: "driver_name,week_start,week_end" });
      if (error) throw error;
      return { success: true, doc };
    } catch (err: any) {
      console.error(err);
      toast({
        title: "Could not save summary",
        description: err?.message ?? String(err),
        variant: "destructive",
      });
      return { success: false, doc: null };
    }
  };

  const handleSave = async () => {
    const { success, doc } = await persistSummary();
    if (sendEmail) {
      await handleSendEmail(doc);
    }
    if (success) {
      toast({
        title: "Summary saved",
        description: `${driverName}'s summary stored — visible on their Reporting tab.`,
      });
    }
  };


  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      {/* Wider content; only vertical scroll so all columns show without horizontal scrolling */}
      <DialogContent className="max-w-6xl p-0">
        <div className="max-h-[80vh] overflow-y-auto">
          <DialogHeader className="px-6 pt-6">
            <DialogTitle>{kpis.kind === "commission" ? "Weekly Summary" : SUMMARY_COPY[kpis.kind].screenTitle} — {driverName}</DialogTitle>
            <DialogDescription>{weekStart} to {weekEnd}</DialogDescription>
          </DialogHeader>

          {loading ? (
            <div className="p-6 text-sm text-muted-foreground">Loading…</div>
          ) : (
            <>
              {/* KPIs */}
              <div className="grid grid-cols-2 md:grid-cols-6 gap-3 px-6">
                <div className="border rounded p-3">
                  <div className="text-xs text-muted-foreground">Turnover</div>
                  <div className="text-lg font-semibold">{money(kpis.gross)}</div>
                </div>
                {kpis.kind !== "lease" && (
                  <div className="border rounded p-3">
                    <div className="text-xs text-muted-foreground">Less GST</div>
                    <div className="text-lg text-red-500">-{money(kpis.gstAmount).replace("$","")}</div>
                  </div>
                )}
                <div className="border rounded p-3">
                  <div className="text-xs text-muted-foreground">{kpis.kind === "lease" ? "Company Share" : kpis.kind === "mixed" ? "Commission & Share" : "Commission"}</div>
                  <div className="text-lg">{money(kpis.commission)}</div>
                </div>
                <div className="border rounded p-3">
                  <div className="text-xs text-muted-foreground">Deductions</div>
                  <div className="text-lg text-red-500">-{money(kpis.deductions).replace("$","")}</div>
                </div>
                {SUMMARY_COPY[kpis.kind].showBonus && (
                  <div className="border rounded p-3">
                    <div className="text-xs text-muted-foreground">Bonus</div>
                    <div className="text-lg">{money(kpis.bonus)}</div>
                  </div>
                )}
                <div className="border rounded p-3">
                  <div className="text-xs text-muted-foreground">Total Payment</div>
                  <div className="text-lg font-bold text-green-500">{money(kpis.net)}</div>
                </div>
              </div>


              {/* Day totals (includes zero days) */}
              <div className="px-6 pb-4 mt-4">
                <div className="border rounded">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Jobs</TableHead>
                        {activeCols.map(c => (
                          <TableHead key={c.key} className="whitespace-nowrap text-center align-bottom">
                            <div className="leading-tight">{c.label}</div>
                            <div className="text-[10px] font-normal text-muted-foreground leading-tight">
                              {colPercentLabel[c.key]}
                            </div>
                          </TableHead>
                        ))}
                        <TableHead>Total</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {days.map(d => (
                        <TableRow key={d.date}>
                          <TableCell>{d.date}</TableCell>
                          <TableCell>{d.jobs}</TableCell>
                          {activeCols.map(c => (
                            <TableCell key={c.key} className={totalsRow.excludedCols.includes(c.key) ? "text-muted-foreground" : ""}>
                              {totalsRow.excludedCols.includes(c.key) ? "–" : money(d.sums[c.key])}
                            </TableCell>
                          ))}
                          <TableCell className="font-medium">{money(d.total)}</TableCell>
                        </TableRow>
                      ))}
                      <TableRow className="font-bold bg-muted/30">
                        <TableCell>TOTAL</TableCell>
                        <TableCell>{totalsRow.jobs}</TableCell>
                        {activeCols.map(c => (
                          <TableCell key={c.key} className={totalsRow.excludedCols.includes(c.key) ? "text-muted-foreground" : ""}>
                            {totalsRow.excludedCols.includes(c.key) ? "–" : money(totalsRow.sums[c.key])}
                          </TableCell>
                        ))}
                        <TableCell>{money(totalsRow.total)}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>
              </div>

              {/* Payment-summary card */}
              <div className="px-6 pb-2">
                <div className="border rounded-lg overflow-hidden max-w-md">
                  <div className="bg-foreground text-background px-4 py-2 text-sm font-semibold">
                    {SUMMARY_COPY[kpis.kind].cardTitle} — {driverName}
                  </div>
                  <div className="px-4 py-3 text-sm space-y-1">
                    {kpis.kind === "lease" ? (
                      kpis.commission > 0.005 && (
                        <>
                          <div className="flex justify-between">
                            <span>Total Collected</span><span>{money(kpis.gross)}</span>
                          </div>
                          <div className="flex justify-between text-red-500">
                            <span>{SUMMARY_COPY[kpis.kind].shareLabel}</span><span>-{money(kpis.commission).replace("$","")}</span>
                          </div>
                        </>
                      )
                    ) : (
                      <>
                        <div className="flex justify-between">
                          <span>Gross Turnover</span><span>{money(kpis.gross)}</span>
                        </div>
                        <div className="flex justify-between text-red-500">
                          <span>Less GST</span><span>-{money(kpis.gstAmount).replace("$","")}</span>
                        </div>
                        <div className="flex justify-between font-semibold border-t pt-1">
                          <span>Net Take</span><span>{money(kpis.netTake)}</span>
                        </div>
                        <div className="flex justify-between text-red-500">
                          <span>{SUMMARY_COPY[kpis.kind].shareLabel}</span><span>-{money(kpis.commission).replace("$","")}</span>
                        </div>
                      </>
                    )}
                    <div className="flex justify-between font-semibold border-t pt-1">
                      <span>{SUMMARY_COPY[kpis.kind].paymentLabel}</span>
                      <span>{money(kpis.driverPayment)}</span>
                    </div>
                    {kpis.segments.map((s) => (
                      <div key={s.label} className="flex justify-between pl-4 text-xs text-muted-foreground">
                        <span>{s.label} <span className="opacity-70">(net take {money(s.netTake)})</span></span>
                        <span>{money(s.driverShare)}</span>
                      </div>
                    ))}


                    {SUMMARY_COPY[kpis.kind].showBonus && (
                      <div className="flex justify-between text-green-600 border-t pt-1">
                        <span>Bonus <span className="text-muted-foreground text-xs">(based on gross)</span></span>
                        <span>+{money(kpis.bonus).replace("$","")}</span>
                      </div>
                    )}
                    <div className="flex justify-between font-semibold border-t pt-1">
                      <span>Deductions</span>
                      <span>{kpis.deductions !== 0 ? "-" : ""}{money(Math.abs(kpis.deductions)).replace("$","$")}</span>
                    </div>
                    {deductionLines.length > 0 ? (
                      deductionLines.map((l, i) => (
                        <div key={i} className="flex justify-between pl-4 text-xs text-muted-foreground">
                          <span>{l.label}</span>
                          <span className={l.amount < 0 ? "text-green-600" : "text-red-500"}>
                            {l.amount < 0 ? "+" : "-"}{money(Math.abs(l.amount)).replace("$","$")}
                          </span>
                        </div>
                      ))
                    ) : (
                      <div className="flex justify-between pl-4 text-xs text-muted-foreground">
                        <span>No deductions</span><span>$0.00</span>
                      </div>
                    )}
                    <div className="flex justify-between font-bold text-green-600 border-t-2 pt-2 mt-1 text-base">
                      <span>Total Payment</span><span>{money(kpis.net)}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Controls */}
              <div className="px-6 pb-6 space-y-3 border-t pt-4">
                <label className="flex items-center gap-2 text-sm">
                  <Checkbox checked={approved} onCheckedChange={v => setApproved(!!v)} />
                  Mark as Approved
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <Checkbox checked={sendEmail} onCheckedChange={v => setSendEmail(!!v)} />
                  Send Email Summary
                </label>
                <Input placeholder="Internal note…" value={note} onChange={e => setNote(e.target.value)} />
                <div className="flex flex-wrap justify-end gap-2">
                  <Button variant="outline" onClick={onClose}>Cancel</Button>
                  <Button variant="secondary" onClick={handleSavePdf} className="flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    Save PDF
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={async () => {
                      const { doc } = await persistSummary();
                      await handleSendEmail(doc);
                    }}
                    disabled={sending}
                    className="flex items-center gap-2"
                  >
                    <Mail className="h-4 w-4" />
                    {sending ? "Sending…" : "PDF + Send Email Summary"}
                  </Button>
                  <Button onClick={handleSave}>Save</Button>
                </div>

              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DriverSummaryModal;
